import { useTranslation } from 'react-i18next';
import type { SubgroupFormData } from '../shared/SubgroupFormConfig';
import SubgroupFormContainer from '../shared/SubgroupFormContainer';
import { CreateSubgroupMutation } from './CreateSubgroup.graphql';
import type { CreateSubgroupMutationVariables } from './CreateSubgroup.graphql';
import client from '@/apolloClient';
import type { GroupListItemDataFragment } from '@/gql/graphql';
import '@/utils/notifications';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';

interface CreateSubgroupProps {
    open: boolean;
    parentGroup: GroupListItemDataFragment | null;
    onClose: () => void;
    onCreated: () => void;
}

const CreateSubgroup = ({ open, onClose, onCreated, parentGroup }: CreateSubgroupProps) => {
    const { t } = useTranslation('groupDetails');

    const handleSubmit = async (formData: SubgroupFormData) => {
        try {
            const variables: CreateSubgroupMutationVariables = {
                input: {
                    name: formData.name,
                    description: formData.description,
                    parentGroupId: parentGroup?.id || '',
                },
            };

            const { data } = await client.mutate({
                mutation: CreateSubgroupMutation,
                variables,
            });

            if (data?.createSubgroup?.__typename === 'CreateSubgroupSuccessfulResponse') {
                if (onCreated) {
                    onCreated();
                } else {
                    onClose();
                }
                showSuccessNotification({
                    message: t('notifications.successTitle'),
                    description: t('notifications.successDescription', { name: formData.name }),
                });
                return;
            }

            if (data?.createSubgroup?.__typename === 'ErrorResponse') {
                const error = {
                    fields: data.createSubgroup.fields,
                };
                showErrorNotification({
                    message: t('notifications.errorTitle'),
                    description: error.fields?.[0]?.message || t('notifications.errorDescription'),
                });
                throw error;
            }

            throw new Error('Unknown error creating group');
        } catch (error) {
            console.error('Error creating group:', error);
        }
    };
    return (
        <SubgroupFormContainer
            mode="create"
            group={parentGroup}
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            onOpenChange={onClose}
            title={t('modalCreateSubgroup.title')}
        />
    );
};

export default CreateSubgroup;
